import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  doc,
  getDoc,
  getFirestore,
} from 'firebase/firestore';
import {
  MdAccountBalance,
  MdCalendarMonth,
  MdCheckCircle,
  MdCreditCard,
  MdFitnessCenter,
  MdHourglassTop,
  MdLocationOn,
  MdOpenInNew,
  MdSavings,
  MdTrendingUp,
  MdWarning,
} from 'react-icons/md';
import Card from 'components/card/Card';
import { AuthContext } from 'contexts/AuthContext';
import { app } from 'lib/firebase';
import {
  formatClassDateTimeLA,
  getClassDisplayText,
  getClassDurationMinutes,
} from 'utils/instructorClassWorkflows';
import {
  getPayoutClassId,
  getPayoutReferenceId,
  getPayoutAmount,
  getPayoutStatus,
  getPayoutStripeTransferStatus,
  getPayoutDateMs,
  getPayoutTransferDateLabel,
  getClassCompletionDate,
  getInstructorPayoutItems,
  getStripeBalanceSummary,
  getStripePaymentMethodSummary,
} from 'utils/instructorPayouts';

const db = getFirestore(app);

const formatCurrency = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const formatDate = (dateValue) => {
  if (!dateValue) return 'Not provided';
  const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'Not provided';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

function PayoutStatusBadge({ status }) {
  const transferStatus = getPayoutStripeTransferStatus({ status });

  return (
    <Badge
      colorScheme={transferStatus.colorScheme}
      borderRadius="8px"
      px="10px"
      py="4px"
      display="inline-flex"
      alignItems="center"
      gap="6px"
    >
      <Icon as={transferStatus.isSuccess ? MdCheckCircle : MdHourglassTop} w="14px" h="14px" />
      {transferStatus.label}
    </Badge>
  );
}

function DetailLine({ icon, label, value }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');

  return (
    <Flex align="flex-start" gap="10px">
      <Icon as={icon} color="brand.500" w="18px" h="18px" mt="2px" flexShrink={0} />
      <Box minW="0">
        <Text color={mutedText} fontSize="xs" fontWeight="800" textTransform="uppercase">
          {label}
        </Text>
        <Text color={textColor} fontSize="sm" fontWeight="700" mt="2px">
          {value}
        </Text>
      </Box>
    </Flex>
  );
}

function StripeSummaryMetric({ icon, label, value }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const metricBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  return (
    <Flex bg={metricBg} borderRadius="14px" p="14px" gap="12px" align="center">
      <Flex w="38px" h="38px" borderRadius="12px" bg="blue.50" align="center" justify="center" flexShrink={0}>
        <Icon as={icon} color="brand.500" w="20px" h="20px" />
      </Flex>
      <Box minW="0">
        <Text color={mutedText} fontSize="xs" fontWeight="800" textTransform="uppercase">
          {label}
        </Text>
        <Text color={textColor} fontSize="lg" fontWeight="900" mt="2px">
          {value}
        </Text>
      </Box>
    </Flex>
  );
}

function StripeAccountSummary({
  stripeStatus,
  stripeSetupLoading,
  openStripeSetup,
  stripeSetupLinkLoading,
}) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const warningBg = useColorModeValue('orange.50', 'whiteAlpha.100');
  const methodSummary = getStripePaymentMethodSummary(stripeStatus);
  const balanceSummary = getStripeBalanceSummary(stripeStatus);
  const methodIcon = methodSummary.type === 'bank' ? MdAccountBalance : MdCreditCard;

  return (
    <Card p="22px" mb="18px" border="1px solid" borderColor={cardBorder}>
      <Flex justify="space-between" gap="14px" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} mb="16px">
        <Box>
          <Text color={textColor} fontSize="xl" fontWeight="900">
            Stripe Account
          </Text>
          <Text color={mutedText} fontSize="sm" mt="4px">
            Review your payout method, current Stripe balance, and lifetime transfer volume.
          </Text>
        </Box>
        <Button
          bg="brand.500"
          color="white"
          borderRadius="12px"
          leftIcon={<MdOpenInNew />}
          isLoading={stripeSetupLinkLoading}
          onClick={openStripeSetup}
          _hover={{ bg: 'brand.600' }}
          _active={{ bg: 'brand.700' }}
        >
          Change Payment Method
        </Button>
      </Flex>

      {stripeSetupLoading ? (
        <Flex align="center" gap="12px" py="18px">
          <Spinner color="brand.500" />
          <Text color={mutedText} fontSize="sm" fontWeight="700">
            Loading Stripe payout details...
          </Text>
        </Flex>
      ) : (
        <SimpleGrid columns={{ base: 1, xl: 2 }} gap="14px">
          <Box border="1px solid" borderColor={cardBorder} borderRadius="16px" p="16px">
            <Flex align="center" gap="12px">
              <Flex w="48px" h="48px" borderRadius="16px" bg="blue.50" align="center" justify="center" flexShrink={0}>
                <Icon as={methodIcon} color="brand.500" w="25px" h="25px" />
              </Flex>
              <Box minW="0">
                <Text color={mutedText} fontSize="xs" fontWeight="800" textTransform="uppercase">
                  Payment Method
                </Text>
                <Text color={textColor} fontSize="lg" fontWeight="900" mt="2px">
                  {methodSummary.title}
                </Text>
                <Text color={mutedText} fontSize="sm" mt="2px">
                  {[methodSummary.detail, methodSummary.meta].filter(Boolean).join(' · ')}
                </Text>
              </Box>
            </Flex>
            {!stripeSetupLoading && methodSummary.expiresSoon && (
              <Flex mt="12px" p="10px" borderRadius="12px" bg={warningBg} gap="8px" align="center">
                <Icon as={MdWarning} color="orange.500" w="18px" h="18px" />
                <Text color={textColor} fontSize="sm" fontWeight="700">
                  This payout card is expired or expires soon.
                </Text>
              </Flex>
            )}
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} gap="12px">
            <StripeSummaryMetric icon={MdSavings} label="Available Balance" value={balanceSummary.available} />
            <StripeSummaryMetric icon={MdHourglassTop} label="Pending Balance" value={balanceSummary.pending} />
            <StripeSummaryMetric icon={MdAccountBalance} label="Total Balance" value={balanceSummary.total} />
            <StripeSummaryMetric
              icon={MdTrendingUp}
              label="All-Time Transfer Volume"
              value={`${balanceSummary.allTimeVolume} · ${balanceSummary.transferCount} transfers`}
            />
          </SimpleGrid>
        </SimpleGrid>
      )}
    </Card>
  );
}

function PayoutClassCard({ payout, classData, facility }) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const iconBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const status = getPayoutStatus(payout);
  const transferStatus = getPayoutStripeTransferStatus(payout);
  const className = getClassDisplayText(
    classData?.ClassName || classData?.ClassType || payout.ClassName || payout.className,
    'Class payout'
  );
  const facilityName = getClassDisplayText(
    facility?.FacilityName || facility?.FirstName || classData?.FacilityName || payout.FacilityName || payout.facilityName,
    'Facility not listed'
  );
  const amount = getPayoutAmount(payout);
  const duration = getClassDurationMinutes(classData || {});
  const completionDate = getClassCompletionDate(classData || {}, payout);
  const payoutDateMs = getPayoutDateMs(payout);
  const payoutDateLabel = payoutDateMs ? formatDate(payoutDateMs) : 'Payout date unavailable';
  const payoutTransferDateLabel = getPayoutTransferDateLabel(payout);

  return (
    <Card p="20px" border="1px solid" borderColor={cardBorder}>
      <Flex direction="column" gap="16px" h="100%">
        <Flex
          data-testid="payout-card-status-row"
          justify={{ base: 'flex-start', md: 'flex-end' }}
          w="100%"
        >
          <PayoutStatusBadge status={status} />
        </Flex>

        <Flex data-testid="payout-card-header-row" align="center" gap="12px" minW="0">
          <Flex w="46px" h="46px" borderRadius="14px" bg={iconBg} align="center" justify="center" flexShrink={0}>
            <Icon as={MdFitnessCenter} color="brand.500" w="24px" h="24px" />
          </Flex>
          <Box minW="0" flex="1">
            <Text color={textColor} fontSize="lg" fontWeight="800" noOfLines={2}>
              {className}
            </Text>
            <Text color={mutedText} fontSize="sm" mt="4px">
              {facilityName}
            </Text>
          </Box>
        </Flex>

        <Box
          border="1px solid"
          borderColor={transferStatus.isSuccess ? 'green.200' : 'orange.200'}
          bg={transferStatus.isSuccess ? 'green.50' : 'orange.50'}
          borderRadius="14px"
          p="12px"
        >
          <Text color={textColor} fontSize="sm" fontWeight="800">
            {transferStatus.isSuccess
              ? `Transferred to Stripe on ${payoutTransferDateLabel}`
              : 'Payout pending'}
          </Text>
          <Text color={mutedText} fontSize="sm" mt="4px" lineHeight="1.45">
            {transferStatus.isSuccess
              ? 'This payout amount has been transferred to your Stripe account. Stripe usually sends it to your bank in 2-3 business days.'
              : 'We are still processing this payout. It will show as transferred to Stripe once the transfer succeeds.'}
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap="14px">
          <DetailLine
            icon={MdCalendarMonth}
            label="Payout Date"
            value={payoutDateLabel}
          />
          <DetailLine
            icon={MdCalendarMonth}
            label="Class Date"
            value={classData?.Date ? formatClassDateTimeLA(classData.Date) : 'Class date unavailable'}
          />
          <DetailLine
            icon={MdFitnessCenter}
            label="Class Type"
            value={getClassDisplayText(classData?.ClassType || classData?.ClassName, 'Class type unavailable')}
          />
          <DetailLine
            icon={MdCheckCircle}
            label="Class Completion"
            value={completionDate ? formatDate(completionDate) : 'Completion form date unavailable'}
          />
          <DetailLine
            icon={MdLocationOn}
            label="Facility"
            value={facilityName}
          />
        </SimpleGrid>

        <Flex
          mt="auto"
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          gap="10px"
          pt="14px"
          borderTop="1px solid"
          borderColor={cardBorder}
        >
          <Box>
            <Text color={mutedText} fontSize="xs" fontWeight="800" textTransform="uppercase">
              Payout Amount
            </Text>
            <Text color={textColor} fontSize="2xl" fontWeight="900" mt="2px">
              {formatCurrency(amount)}
            </Text>
          </Box>
          <Flex gap="8px" wrap="wrap">
            {duration > 0 && (
              <Badge colorScheme="blue" borderRadius="8px" px="10px" py="4px">
                {duration} min
              </Badge>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
}

export async function loadPayoutClassDetails(payoutItems, firestoreApi = {}) {
  const dbInstance = firestoreApi.db || db;
  const docFn = firestoreApi.doc || doc;
  const getDocFn = firestoreApi.getDoc || getDoc;
  const payoutReferenceIds = Array.from(new Set(
    payoutItems.map(getPayoutReferenceId).filter(Boolean)
  ));

  const payoutReferenceEntries = await Promise.all(payoutReferenceIds.map(async (payoutReferenceId) => {
    const payoutSnap = await getDocFn(docFn(dbInstance, 'Payouts', payoutReferenceId));
    return [
      payoutReferenceId,
      payoutSnap.exists() ? { id: payoutReferenceId, ...payoutSnap.data() } : null,
    ];
  }));
  const payoutsByReferenceId = Object.fromEntries(payoutReferenceEntries);
  const enrichedPayoutItems = payoutItems.map((payout) => {
    const payoutReferenceId = getPayoutReferenceId(payout);
    return {
      ...(payoutReferenceId ? payoutsByReferenceId[payoutReferenceId] || {} : {}),
      ...payout,
    };
  });

  const classIds = Array.from(new Set(
    enrichedPayoutItems.map(getPayoutClassId).filter(Boolean)
  ));

  const classEntries = await Promise.all(classIds.map(async (classId) => {
    const classSnap = await getDocFn(docFn(dbInstance, 'Classes', classId));
    return [classId, classSnap.exists() ? { id: classId, ...classSnap.data() } : null];
  }));

  const classesById = Object.fromEntries(classEntries);
  const facilityIds = Array.from(new Set(
    Object.values(classesById)
      .map((classData) => classData?.FacilityID)
      .filter(Boolean)
  ));

  const facilityEntries = await Promise.all(facilityIds.map(async (facilityId) => {
    const facilitySnap = await getDocFn(docFn(dbInstance, 'Users', facilityId));
    return [facilityId, facilitySnap.exists() ? facilitySnap.data() : null];
  }));

  return {
    classesById,
    facilitiesById: Object.fromEntries(facilityEntries),
    payoutsByReferenceId,
  };
}

export default function InstructorPayouts() {
  const {
    userData,
    stripeStatus,
    stripeSetupLoading,
    openStripeSetup,
    stripeSetupLinkLoading,
  } = useContext(AuthContext);
  const [classesById, setClassesById] = useState({});
  const [facilitiesById, setFacilitiesById] = useState({});
  const [payoutsByReferenceId, setPayoutsByReferenceId] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const brandColor = useColorModeValue('brand.500', 'brand.400');

  const payoutItems = useMemo(() => (
    getInstructorPayoutItems(userData).filter((payout) => payout.payoutState !== 'reversed')
  ), [userData]);

  useEffect(() => {
    let isActive = true;

    const loadDetails = async () => {
      setIsLoading(true);
      try {
        const details = await loadPayoutClassDetails(payoutItems);
        if (!isActive) return;
        setClassesById(details.classesById);
        setFacilitiesById(details.facilitiesById);
        setPayoutsByReferenceId(details.payoutsByReferenceId);
      } catch (error) {
        console.error('Error loading payout class details:', error);
        if (!isActive) return;
        setClassesById({});
        setFacilitiesById({});
        setPayoutsByReferenceId({});
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    loadDetails();

    return () => {
      isActive = false;
    };
  }, [payoutItems]);

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="instructor-payouts-page">
      <Box id="payout-summary-widget">
        <StripeAccountSummary
          stripeStatus={stripeStatus}
          stripeSetupLoading={stripeSetupLoading}
          openStripeSetup={openStripeSetup}
          stripeSetupLinkLoading={stripeSetupLinkLoading}
        />
      </Box>

      <Card id="payout-history-widget" p="22px">
        <Flex align="center" justify="space-between" gap="12px" mb="18px" wrap="wrap">
          <Box>
            <Text color={textColor} fontSize="xl" fontWeight="800">
              Payout History
            </Text>
            <Text color={mutedText} fontSize="sm" mt="4px">
              Class details are matched from Classes and facility profiles in Users.
            </Text>
          </Box>
          <Badge colorScheme="blue" borderRadius="8px" px="10px">
            {payoutItems.length} payouts
          </Badge>
        </Flex>

        {isLoading ? (
          <Flex align="center" gap="12px" py="28px">
            <Spinner color={brandColor} />
            <Text color={mutedText}>Loading payout class details...</Text>
          </Flex>
        ) : payoutItems.length === 0 ? (
          <Box py="28px">
            <Text color={mutedText}>No payouts on record yet.</Text>
          </Box>
        ) : (
          <SimpleGrid columns={{ base: 1, xl: 2 }} gap="16px">
            {payoutItems.map((payout, index) => {
              const payoutReferenceId = getPayoutReferenceId(payout);
              const hydratedPayout = {
                ...(payoutReferenceId ? payoutsByReferenceId[payoutReferenceId] || {} : {}),
                ...payout,
              };
              const classId = getPayoutClassId(hydratedPayout);
              const classData = classesById[classId];
              const facility = classData?.FacilityID ? facilitiesById[classData.FacilityID] : null;

              return (
                <PayoutClassCard
                  key={`${classId || payout.payoutReferenceID || index}-${index}`}
                  payout={hydratedPayout}
                  classData={classData}
                  facility={facility}
                />
              );
            })}
          </SimpleGrid>
        )}
      </Card>
    </Box>
  );
}
